import { Button } from '@/components/Button'
import { Card, CardBody, CardHeader } from '@/components/Card'
import { Skeleton } from '@/components/Skeleton'
import { useAuth } from '@/hooks/useAuth'
import { usePushSubscription } from '@/hooks/usePushSubscription'

export function PushCard() {
  const { features } = useAuth()
  const push = usePushSubscription()

  if (features && !features.push) {
    return (
      <Card>
        <CardHeader title="Push notifications" />
        <CardBody>
          <p className="text-sm text-muted">
            Push is not configured on this server yet, so there is nothing to turn on here.
          </p>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader
        title="Push notifications"
        description="Get told when a request is approved, starts downloading or is ready."
      />
      <CardBody className="space-y-4">
        {push.status === 'checking' ? (
          <Skeleton className="h-10 w-48" />
        ) : push.status === 'unsupported' ? (
          <p className="text-sm text-muted">
            This browser does not support push notifications. Chrome, Edge, Firefox and Safari 16.4+
            do.
          </p>
        ) : push.status === 'needs-install' ? (
          <div className="space-y-2 text-sm text-muted">
            <p className="signage text-[10px] text-ink">One step first on iPhone and iPad</p>
            <ol className="list-inside list-decimal space-y-1">
              <li>Tap the Share button in Safari.</li>
              <li>Choose “Add to Home Screen”.</li>
              <li>Open Hermes from the new icon and come back here.</li>
            </ol>
            {/* iOS only exposes the Push API to installed PWAs — there is no way
                around this from a normal Safari tab. */}
            <p className="text-xs text-faint">
              Apple only allows notifications for apps added to the Home Screen.
            </p>
          </div>
        ) : push.status === 'denied' ? (
          <p className="text-sm text-muted">
            Notifications are blocked for this site. Re-allow them in your browser’s site settings,
            then reload this page.
          </p>
        ) : push.status === 'subscribed' ? (
          <div className="flex flex-wrap items-center gap-3">
            {/* Lamp + signage: the same "on" reading used on the status board. */}
            <span className="signage inline-flex items-center gap-2 text-[10px] text-ok">
              <span aria-hidden="true" className="size-1.5 rounded-full bg-ok" />
              On for this device
            </span>
            <div className="ml-auto flex gap-2">
              <Button size="sm" onClick={() => void push.sendTest()} loading={push.isBusy}>
                Send test
              </Button>
              <Button size="sm" variant="ghost" onClick={() => void push.disable()}>
                Turn off
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <span className="signage inline-flex items-center gap-2 text-[10px] text-faint">
              <span aria-hidden="true" className="size-1.5 rounded-full bg-faint" />
              Off on this device
            </span>
            <Button
              className="ml-auto"
              variant="primary"
              size="sm"
              loading={push.isBusy}
              onClick={() => void push.enable()}
            >
              Turn on notifications
            </Button>
          </div>
        )}

        <p className="text-xs text-faint">
          Notifications are per device. Turn them on anywhere you want to be told.
        </p>
      </CardBody>
    </Card>
  )
}
